/* eslint-disable @repo/internal/deprecations/deprecation-ticket-required */
import memoize from 'lodash/memoize';
import {
	type Action,
	createActionsHook,
	createContainer,
	createHook,
	createSelector,
	createStateHook,
	createStore,
} from 'react-sweet-state';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { type EntityATI, type TProductEdition as ProductEdition, type ThirdPartyFilters } from '@atlassian/search-client';
import {
	type BackendExperiment,
	type ExperimentConfig,
	type FrontendExperiments,
	getBackendExperiment,
} from '@atlassian/search-experiment';

import { ApplicationModes } from '../../../common/constants/application-modes';
import { type BootstrapState, type ClearReferrerCallback, initialState, type RefreshSearchSessionIdCallback, type SetQueryParamsCallback } from '../../../common/constants/bootstrap';
import { SEARCH_PAGE } from '../../../common/constants/experience';
import { TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import type { NounKeys } from '../../../common/constants/nouns';
import { ProductKeys } from '../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import {
	productKeySchema,
	type SearchQueryStringParams,
	type SearchQueryStringParamsRaw,
} from '../../../common/constants/schemas/query-params';
import {
	type UserDetailsWithPicture,
	type UserDetailsWithStatus,
} from '../../../common/constants/types';
import { getApplicationMode, getUser } from '../../../common/utils/bootstrap';
import {
	getMultiSiteSearchInRovo,
	getUserShardDebounceTimeout,
	serveBootstrapProviderExp,
	serveUserShardModel,
	serveUserShardUI,
} from '../../../common/utils/experiments';
import { isHello } from '../../../common/utils/is-hello';
import {
	areAllEqualQueryParams,
	parseQueryParams,
	toQueryParamsRaw,
	toQueryString,
} from '../../../common/utils/query-params';

import {
	type HookReturn,
	useAnalyticsAttributesContext,
	useApplicationModeContext,
	useAvailableNounsContext,
	useAvailableProductsContext,
	useBackendExperimentContext,
	useExperimentConfigContext,
	useFiltersDiscoverability2EnabledContext,
	useFrontendExperimentsContext,
	useInitialTextQueryParamContext,
	useIntercomHmacContext,
	useIsAvailableProductsLoadedContext,
	useIsConfigLoadedContext,
	useIsHelloContext,
	useIsNounsAvailableContext,
	useIsRovoFreeContext,
	useLinkedCloudARIsContext,
	useMultiSiteSearchInRovoContext,
	useProductEditionContext,
	useReferrerContext,
	useSearchSessionIdContext,
	useThirdPartyConfigsContext,
} from './bootstrap-context-provider/accessors';
import { useClearReferrerContext, useRefreshSearchSessionIdContext, useSetQueryParamsContext } from './bootstrap-context-provider/actions';

export const actions = {
	setState:
		(partialState: Partial<BootstrapState>): Action<BootstrapState> =>
			({ setState }) => {
				setState(partialState);
			},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const setQueryParams = useSetQueryParams()`
	 */
	setQueryParams:
		(
			queryParam: Partial<SearchQueryStringParams>,
		): Action<BootstrapState, BootstrapContainerProps> =>
			({ }, { queryParams, queryParamsCallback = () => { } }) => {
				const areEqualQueryParams = areAllEqualQueryParams(
					queryParam,
					parseQueryParams(queryParams ?? {}),
				);

				if (areEqualQueryParams) {
					return;
				}

				queryParamsCallback(toQueryParamsRaw(queryParam));
			},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 *
	 * You should not ever need to call this directly. Open a thread in #search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
	 */
	syncQueryParams:
		(): Action<BootstrapState, BootstrapContainerProps> =>
			({ setState, getState }, { queryParams, queryParamsCallback = () => { } }) => {
				// queryParams is the query params coming from the URL or React Router in this case
				const {
					prevQueryParams, // Previously saved query params - this is to prevent infinitely updating queryParams
					queryParams: diffQueryParamsState, // The 'internal' query params state
				} = getState();

				const queryParamsURL = parseQueryParams(queryParams ?? {});

				const prevQueryParamsString = toQueryString(prevQueryParams);
				const diffQueryParamsURLString = toQueryString(queryParamsURL);
				const diffQueryParamsStateString = toQueryString(diffQueryParamsState);

				const allQueryParamsAreEqual = areAllEqualQueryParams(
					queryParamsURL,
					prevQueryParams,
					diffQueryParamsState,
				);

				if (allQueryParamsAreEqual) {
					return;
				}

				// Any changes from the url takes the highest precedence
				const hasURLQueryParams = !!queryParams;
				if (hasURLQueryParams && prevQueryParamsString !== diffQueryParamsURLString) {
					setState({ prevQueryParams: queryParamsURL, queryParams: queryParamsURL });
					return;
				}

				if (prevQueryParamsString !== diffQueryParamsStateString) {
					//  NOTE: If you are running the callback and you don't see the query params being updated,
					//  the callback might be sanitising the query parameters. This is the case for Confluence.
					queryParamsCallback(toQueryParamsRaw(diffQueryParamsState));
					setState({ prevQueryParams: diffQueryParamsState, queryParams: diffQueryParamsState });
					return;
				}
			},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
	 */
	refreshSearchSessionId:
		(): Action<BootstrapState, BootstrapContainerProps> =>
			({ setState }) => {
				// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
				setState({ searchSessionId: uuid() });
			},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const clearReferrer = useClearReferrer()`
	 */
	clearReferrer:
		(): Action<BootstrapState, BootstrapContainerProps> =>
			({ setState }) => {
				setState({ referrer: undefined });
			},
};

const buildProducts = memoize(
	(a: ProductKeys[], b: ProductKeys[]) => {
		return [...new Set([...a, ...b])];
	},
	(a: ProductKeys[], b: ProductKeys[]) => a.join('') + b.join(''),
);

type TActions = typeof actions;

// Keep store private and use hooks to access state and actions
const Store = createStore<BootstrapState, TActions>({ initialState, actions, name: 'bootstrap' });

export const bootstrapStoreContainerActions = {
	onInit:
		(): Action<BootstrapState, BootstrapContainerProps> =>
			({ getState, setState }, { initialProps, onSearchCompleted, queryParams }) => {
				const parsedQueryParams = queryParams && parseQueryParams(queryParams);
				setState({
					...getState(),
					...initialProps,
					...(queryParams && { queryParams: parsedQueryParams }),
					referrer: parsedQueryParams?.referrer,
					initialTextQueryParam: parsedQueryParams?.text,
					// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
					searchSessionId: initialProps.searchSessionId || uuid(),
					onSearchCompleted,
				});
			},
	onUpdate:
		(): Action<BootstrapState, BootstrapContainerProps> =>
			({ setState, getState, dispatch }, { initialProps, onSearchCompleted, queryParams }) => {
				const parsedQueryParams = queryParams && parseQueryParams(queryParams);

				const products = buildProducts(getState().products, initialProps?.products ?? []);

				setState({
					...getState(),
					...initialProps,
					onSearchCompleted,
					products,
					referrer: parsedQueryParams?.referrer,
				});

				dispatch(actions.syncQueryParams());
			},
};

export type QueryParamsCallback = (queryParams: Partial<SearchQueryStringParamsRaw>) => void;

export type BootstrapContainerProps = {
	initialProps: Partial<BootstrapState>;
	queryParams?: Record<string, any>;
	/** Called when FPS query params should be updated in the application's router. */
	queryParamsCallback?: QueryParamsCallback;
	/**
	 * @private
	 * @deprecated this prop is deprecated and not used anymore. It will be removed soon.
	 *
	 * @returns the space IDs for the given space keys. NOTE: these should be the space IDs, not ARIs.
	 * @example getSpaceIdsFromSpaceKeys(["STATUS"]); // Promise<["123454321"]>
	 **/
	getSpaceIdsFromSpaceKeys?: (spaceKeys: string[]) => Promise<string[]>;
	/** Called when the Quick Find search completes successfully. */
	onSearchCompleted?: (resultCount?: number, query?: string) => void;
};

export const BootstrapStoreContainer = createContainer<
	BootstrapState,
	TActions,
	BootstrapContainerProps
>(Store, bootstrapStoreContainerActions);

// Hooks

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * This is being actively migrated away from in QS-9194
 *
 * Instead use specific per-action accessors from this file if they are present
 *
 * e.g. const { setQueryParams } = useBootstrapActions();
 * -> const setQueryParams = useSetQueryParams();
 */
export const useBootstrapActions = createActionsHook(Store);

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * This is being actively migrated away from in QS-9194
 *
 * Instead use specific per-value accessors from this file, including for actions if they are present
 *
 * e.g. const [{ useCloudId, useQueryParams }, { useSetQueryParams }] = useBootstrap()
 * -> const [cloudId] = useCloudId();
 * -> const [queryParams] = useQueryParams();
 * -> const setQueryParams = useSetQueryParams();
 */
export const useBootstrap = createHook(Store);

/**
 * @deprecated
 * See useBootstrapState in bootstrap/accessors/index.tsx
 */
export const useBootstrapStateStore = createStateHook(Store, {
	selector: (state: BootstrapState, cb: (state: BootstrapState) => unknown) => cb(state),
}) as unknown as <T>(cb: (state: BootstrapState) => T) => T;

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAnalyticsAttributes in bootstrap/index.tsx
 *
 * This hook gets the extra analytics attributes that are passed in as a prop from the consumer.
 * It should not be confused with the hooks for firing analytics events in `controllers/use-analytics`
 */
export const useAnalyticsAttributesStore = createHook(Store, {
	selector: (state) => state.analyticsAttributes,
});

/**
 * Retrieve analytics attributes from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAnalyticsAttributes = (): HookReturn<Record<string, any> | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [useAnalyticsAttributesContext()]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useAnalyticsAttributesStore()[0]] satisfies HookReturn<Record<string, any> | undefined>;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAvailableProducts in bootstrap/index.tsx
 */
export const useAvailableProductsStore = createStateHook(Store, { selector: (state) => state.products });

/**
 * Retrieve available products from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAvailableProducts = (): ProductKeys[] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useAvailableProductsContext() : useAvailableProductsStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAvailableNouns in bootstrap/index.tsx
 */
export const useAvailableNounsStore = createStateHook(Store, { selector: (state) => state.nouns });

/**
 * Retrieve available nouns from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAvailableNouns = (): NounKeys[] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useAvailableNounsContext() : useAvailableNounsStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsAvailableProductsLoaded in bootstrap/index.tsx
 */
export const useIsAvailableProductsLoadedStore = createStateHook(Store, {
	selector: (state) => state.products && state.products.length > 0,
});

/**
 * Retrieve whether available products have been loaded from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsAvailableProductsLoaded = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useIsAvailableProductsLoadedContext() : useIsAvailableProductsLoadedStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useBootstrapLoading in bootstrap/index.tsx
 */
export const useBootstrapLoadingStore = createHook(Store, {
	selector: (state) => state.products.length === 0,
});

/**
 * Retrieve whether bootstrap is still loading (products not yet available) from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useBootstrapLoading = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [!useIsAvailableProductsLoadedContext()]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useBootstrapLoadingStore()[0]] satisfies HookReturn<boolean>;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use usePrimaryProduct in bootstrap/accessors/index.tsx
 */
export const usePrimaryProductStore = createHook(Store, { selector: (state) => state.primaryProduct });

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useSearchSessionId in bootstrap/index.tsx
 */
export const useSearchSessionIdStore = createHook(Store, { selector: (state) => state.searchSessionId });

/**
 * Retrieve the search session ID from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSearchSessionId = (): HookReturn<string> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [useSearchSessionIdContext()]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useSearchSessionIdStore()[0]] satisfies HookReturn<string>;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsHello in bootstrap/index.tsx
 */
export const useIsHelloStore = createHook(Store, { selector: (state) => isHello(state.cloudId) });

/**
 * Retrieve whether the current tenant is Hello from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsHello = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [useIsHelloContext()]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useIsHelloStore()[0]] satisfies HookReturn<boolean>;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useFeatures in bootstrap/accessors/index.tsx
 */
export const useFeaturesStore = createHook(Store, { selector: (state) => state.features });

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useBackendExperiment in bootstrap/index.tsx
 */
export const useBackendExperimentStore = createHook(Store, {
	selector: (state, args: { entities: EntityATI[]; thirdPartyFilters: ThirdPartyFilters }) => {
		return getBackendExperiment({
			experimentConfig: state.experimentConfig,
			entities: args.entities,
			productEdition: state.productEdition,
			isHello: isHello(state.cloudId),
			productDataRegions: state.productDataRegions,
			experience: SEARCH_PAGE,
			tenantId: state.cloudId,
			isJiraIssueIndexed: state.isJiraIssueIndexed,
			thirdPartyFilters: args.thirdPartyFilters,
		});
	},
});

/**
 * Retrieve the backend experiment from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useBackendExperiment = (args: { entities: EntityATI[]; thirdPartyFilters: ThirdPartyFilters }): HookReturn<BackendExperiment> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [useBackendExperimentContext(args)]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useBackendExperimentStore(args)[0]] satisfies HookReturn<BackendExperiment>;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useFrontendExperiments in bootstrap/index.tsx
 */
export const useFrontendExperimentsStore = createHook(Store, {
	selector: (state) => state.experimentConfig.frontendExperiments,
});

/**
 * Retrieve frontend experiments from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useFrontendExperiments = (): HookReturn<FrontendExperiments> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [useFrontendExperimentsContext()]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useFrontendExperimentsStore()[0]] satisfies HookReturn<FrontendExperiments>;
};


export const useMultiSiteSearchInRovoStore = createStateHook(Store, {
	selector: (state) => getMultiSiteSearchInRovo(state.experimentConfig),
});

/**
 * Retrieve whether multi-site search in Rovo is enabled from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useMultiSiteSearchInRovo = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useMultiSiteSearchInRovoContext() : useMultiSiteSearchInRovoStore();
};

export const useCurrentUserId = createStateHook(Store, {
	selector: (state) => state.user?.id,
});

export const useUserShardModel = createStateHook(Store, {
	selector: () => serveUserShardModel(),
});

export const useUserShardUI = createStateHook(Store, {
	selector: () => serveUserShardUI(),
});

export const useUserShardDebounceTimeout = createStateHook(Store, {
	selector: () => getUserShardDebounceTimeout(),
});

/* <<< Confluence QuickFind UserShard Search Experiments */

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useExperimentConfig in bootstrap/index.tsx
 */
export const useExperimentConfigStore = createHook(Store, {
	selector: (state) => state.experimentConfig,
});

/**
 * Retrieve the experiment config from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useExperimentConfig = (): HookReturn<ExperimentConfig> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [useExperimentConfigContext()]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useExperimentConfigStore()[0]] satisfies HookReturn<ExperimentConfig>;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIntercomHmac in bootstrap/index.tsx
 */
export const useIntercomHmacStore = createHook(Store, { selector: (state) => state.intercomHmac });

/**
 * Retrieve Intercom HMAC from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIntercomHmac = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [useIntercomHmacContext()]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useIntercomHmacStore()[0]] satisfies HookReturn<string | undefined>;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsNounsAvailable in bootstrap/index.tsx
 */
export const useIsNounsAvailableStore = createHook(Store, {
	selector: (state) => state.isNounsAvailable,
});

/**
 * @deprecated
 * https://hello.atlassian.net/wiki/x/bKRdZAE
 */
const useApplicationModeStore = createStateHook(Store, {
	selector: createSelector(
		[(state) => state.isRovoEnabled],
		(isRovoEnabled): ApplicationModes => getApplicationMode({ isRovoEnabled }),
	),
});

/**
 * Retrieve the current application mode (Rovo or Unified).
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useApplicationMode = (): ApplicationModes => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useApplicationModeContext() : useApplicationModeStore();
};

/**
 * Retrieve linked cloud ARIs.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useLinkedCloudARIs = (): [string[] | undefined] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? [useLinkedCloudARIsContext()] : [useLinkedCloudARIsStore()[0]];
};

/**
 * Retrieve the initial text query param.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useInitialTextQueryParam = (): string | undefined => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useInitialTextQueryParamContext() : useInitialTextQueryParamStore();
};

/**
 * Retrieve the referrer.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useReferrer = (): [string | undefined] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? [useReferrerContext()] : [useReferrerStore()[0]];
};

/**
 * Retrieve whether nouns are available.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsNounsAvailable = (): [boolean] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? [useIsNounsAvailableContext()] : [useIsNounsAvailableStore()[0]];
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useUser in bootstrap/accessors/index.tsx
 */
export const useUserStore = createHook(Store, {
	selector: (state): UserDetailsWithStatus => {
		const user = state.user satisfies UserDetailsWithPicture | undefined;
		return getUser(user);
	},
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useReferrer in bootstrap/index.tsx
 */
export const useReferrerStore = createHook(Store, { selector: (state) => state.referrer });

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useInitialTextQueryParam in bootstrap/index.tsx
 */
export const useInitialTextQueryParamStore = createStateHook(Store, {
	selector: (state) => state.initialTextQueryParam,
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useOrgId in bootstrap/accessors/index.tsx
 */
export const useOrgIdStore = createHook(Store, { selector: (state) => state.orgId });

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useCloudId in bootstrap/accessors/index.tsx
 */
export const useCloudIdStore = createHook(Store, {
	selector: createSelector([(state) => state.cloudId], (cloudId) => cloudId),
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useLinkedCloudARIs in bootstrap/index.tsx
 */
export const useLinkedCloudARIsStore = createHook(Store, { selector: (state) => state.linkedCloudARIs });

export const useAutoSelectPrimaryProduct = createStateHook(Store, {
	selector: createSelector(
		[
			(state) => state.products,
			(state) => state.primaryProduct,
			(state) => state.isRovoEnabled,
			(state) => state.queryParams.product,
			(state) => state.queryParams.cql,
		],
		(products, primaryProduct, isRovoEnabled, currentSelectedProduct, currentCql) => {
			const applicationMode = getApplicationMode({ isRovoEnabled });
			const primaryProductToSelect = productKeySchema.safeParse(primaryProduct);

			const shouldAutoSelectPrimaryProduct =
				!currentSelectedProduct &&
				!currentCql &&
				products &&
				products.length > 0 &&
				applicationMode === ApplicationModes.Unified &&
				primaryProductToSelect.success &&
				primaryProductToSelect.data !== ProductKeys.Atlas;

			if (shouldAutoSelectPrimaryProduct) {
				return primaryProductToSelect.data;
			} else {
				return null;
			}
		},
	),
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsConfigLoaded in bootstrap/index.tsx
 */
export const useIsConfigLoadedStore = createStateHook(Store, {
	selector: createSelector([(state) => state.isConfigLoaded], (isConfigLoaded) => isConfigLoaded),
});

/**
 * Retrieve whether the bootstrap config has finished loading from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsConfigLoaded = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useIsConfigLoadedContext() : useIsConfigLoadedStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsRovoFree in bootstrap/index.tsx
 * Note: this hook is only temporary and should be removed once Rovo is enabled for all users
 */
export const useIsRovoFreeStore = createHook(Store, { selector: (state) => !!state.rovoFeatures?.scas });

/**
 * Retrieve whether Rovo is free from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 * Note: this hook is only temporary and should be removed once Rovo is enabled for all users
 */
export const useIsRovoFree = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [useIsRovoFreeContext()]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useIsRovoFreeStore()[0]] satisfies HookReturn<boolean>;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useProductEdition in bootstrap/index.tsx
 */
export const useProductEditionStore = createHook(Store, { selector: (state) => state.productEdition });

/**
 * Retrieve product edition from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useProductEdition = (): (ProductEdition | undefined)[] => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [useProductEditionContext()]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useProductEditionStore()[0]] satisfies (ProductEdition | undefined)[];
};

export const useIsTypeFilteredToIncident = createStateHook(Store, {
	selector: (state) => state.queryParams?.type === TypeFilterValueKey.Incident,
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useThirdPartyConfigs in bootstrap/index.tsx
 */
export const useThirdPartyConfigsStore = createHook(Store, {
	selector: (state) => state.thirdPartyConfigs,
});

/**
 * Retrieve third party configs from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useThirdPartyConfigs = (): HookReturn<ThirdPartyConfigsBootstrap> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? [useThirdPartyConfigsContext()]
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: [useThirdPartyConfigsStore()[0]] satisfies HookReturn<ThirdPartyConfigsBootstrap>;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useFiltersDiscoverability2Enabled in bootstrap/index.tsx
 */
export const useFiltersDiscoverability2EnabledStore = createStateHook(Store, {
	selector: (state) => {
		const primaryProduct = state.primaryProduct;
		if (!primaryProduct) {
			return false;
		}

		return primaryProduct === ProductKeys.Confluence || primaryProduct === ProductKeys.Jira;
	},
});

/**
 * Retrieve whether filters discoverability 2 is enabled from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useFiltersDiscoverability2Enabled = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? useFiltersDiscoverability2EnabledContext()
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: useFiltersDiscoverability2EnabledStore();
};

/**
 * Retrieve the callback to set query parameters into the URL (to commit a filter selection). Accepts the new parameter to set and merges with the current query parameters.
 *
 * The callback is expected to update the query parameters in the URL first, and then the URL change propagates back to FPS where it triggers a store update accordingly.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSetQueryParams = (): SetQueryParamsCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { setQueryParams: setQueryParamsStore } = useBootstrapActions();
	const setQueryParamsContext = useSetQueryParamsContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? setQueryParamsContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: setQueryParamsStore;
}

/**
 * Retrieve the callback to refresh the search session ID. This is used when we want to generate a new search session ID without remounting the entire bootstrap provider, such as when the user clicks on the "Search" button in Quick Find. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useRefreshSearchSessionId = (): RefreshSearchSessionIdCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { refreshSearchSessionId: refreshSearchSessionIdStore } = useBootstrapActions();
	const refreshSearchSessionIdContext = useRefreshSearchSessionIdContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? refreshSearchSessionIdContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: refreshSearchSessionIdStore;
}

export const useClearReferrer = (): ClearReferrerCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { clearReferrer: clearReferrerStore } = useBootstrapActions();
	const clearReferrerContext = useClearReferrerContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		? clearReferrerContext
		// eslint-disable-next-line react-hooks/rules-of-hooks
		: clearReferrerStore;
}
